import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import "./PersonalDetails.css";
import { baseUrl, documentsList } from "../../../Utility/constants";

const DocumentMasterModal = ({
  showhandleDocumentMaster,
  handleDocumentMasterClose,
  studentId,
  studentDocumentId,
  refreshList
}) => {
  const role = localStorage.getItem("Role");
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [studentDocuments, setStudentDocuments] = useState([]);
  const [lcRemarks, setLcRemarks] = useState(""); // ✅ NEW


  const docLabelMap = Object.fromEntries(
    documentsList.map((d) => [d.id, d.label])
  );

  useEffect(() => {
    if (studentId) {
      fetchStudentDocuments();
    }
  }, [studentId]);

  const fetchStudentDocuments = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/StudentDocumentsMaster/by-student/${studentId}`
      );
      setStudentDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    const isPhoto = name.toLowerCase().includes("photo");
    const isSignature = name.toLowerCase().includes("signature");

    // Validate JPEG for photo/signature
    if (
      (isPhoto || isSignature) &&
      !file.type.includes("jpeg") &&
      !file.type.includes("jpg")
    ) {
      toast.warning(`${file.name} must be in JPEG format.`);
      e.target.value = "";
      return;
    }

    // Validate Photo
    if (isPhoto) {
      if (file.size < 25 * 1024 || file.size > 40 * 1024) {
        toast.warning(`Photo size must be between 25 KB and 40 KB.`);
        e.target.value = "";
        return;
      }
    }

    // Validate Signature
    if (isSignature) {
      if (file.size < 10 * 1024 || file.size > 20 * 1024) {
        toast.warning(`Signature size must be between 10 KB and 20 KB.`);
        e.target.value = "";
        return;
      }
    }

    // Other docs - max 512KB
    if (!isPhoto && !isSignature && file.size > 512 * 1024) {
      toast.warning(`${file.name} exceeds 512 KB size limit.`);
      e.target.value = "";
      return;
    }

    setUploadedFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const isAnyFileUploaded = Object.keys(uploadedFiles).length > 0;

    if (!isAnyFileUploaded) {
      toast.warning("Please upload at least one document.");
      return;
    }

    const formData = new FormData();
    formData.append("studentId", studentId || 0);
    formData.append("studentDocumentId", studentDocumentId || 0);

    // append files
    for (const [key, file] of Object.entries(uploadedFiles)) {
      formData.append(key, file);
    }

    // ✅ append LC remarks
    formData.append("lcRemarks", lcRemarks);

    try {
      const response = await axios.post(
        `${baseUrl}/api/StudentDocumentsMaster/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Documents uploaded successfully!");
        handleDocumentMasterClose();
        if (refreshList) refreshList();
      } else {
        toast.error("Failed to upload documents.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while uploading documents.");
    }
  };

  const renderUploadSection = (categoryName) => (
    <Row className="border rounded mb-2">
      <h4 className="mt-1">{categoryName}</h4>

      {documentsList
        .filter((doc) => doc.category === categoryName)
        .map((doc, index) => (
          <Col md={2} key={index} className="mb-2">
            <Form.Group controlId={`form-${doc.id}`}>
              <Form.Label>{doc.label}</Form.Label>
              <Form.Control
                type="file"
                name={doc.id}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Max file size:{" "}
                {doc.label === "Photo"
                  ? "20 to 50 KB"
                  : doc.label === "Signature"
                    ? "10 to 20 KB"
                    : "512 KB"}
              </Form.Text>
            </Form.Group>
          </Col>
        ))}
    </Row>
  );

  const renderUploadedSection = (categoryName) => {
    const filteredDocs = studentDocuments.filter((doc) =>
      documentsList.find(
        (d) => d.id === doc.documentType && d.category === categoryName
      )
    );

    if (filteredDocs.length === 0) return null;

    return (
      <div className="border rounded p-2 mb-1">
        <h4>{categoryName}</h4>
        <Row>
          {filteredDocs.map((doc, index) => (
            <Col md={2} key={index}>
              <strong>
                {docLabelMap[doc.documentType] || doc.documentType}
              </strong>
              <br />
              <a
                href={doc.filePath}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                View Document
              </a>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <Modal
      show={showhandleDocumentMaster}
      onHide={handleDocumentMasterClose}
      dialogClassName="custom-modal-lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload Documents</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleFormSubmit}>
          {renderUploadSection("Academic Records")}
          {renderUploadSection("KYC Details")}
          {renderUploadSection("Special Cases/Reservations")}

          {/* ✅ NEW LC SECTION */}
          {role !== 'STUDENT' ?
            <Row className="border rounded mb-2">
              <h4 className="mt-1">LC Corrections Details</h4>

              <Col md={4} className="mb-2">
                <Form.Group>
                  <Form.Label>Correction Reason</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter remarks"
                    value={lcRemarks}
                    onChange={(e) => setLcRemarks(e.target.value)}
                    disabled={role === "TEACHER" && localStorage.getItem("IsClassTeacher") === 'true'}
                  />
                </Form.Group>
              </Col>

              <Col md={4} className="mb-2">
                <Form.Group>
                  <Form.Label>Approval Letter/Previous Updated LC</Form.Label>
                  <Form.Control
                    type="file"
                    name="lcDocumentFile"
                    onChange={handleInputChange}
                    disabled={role === "TEACHER" && localStorage.getItem("IsClassTeacher") === 'true'}
                  />
                  <Form.Text className="text-muted">
                    Max file size: 512 KB
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            : ""}

          <Modal.Footer>
            <Button variant="secondary" onClick={handleDocumentMasterClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>

        <hr />
        <h5>Uploaded Documents</h5>

        {studentDocuments.length > 0 ? (
          <>
            {renderUploadedSection("Academic Records")}
            {renderUploadedSection("KYC Details")}
            {renderUploadedSection("Special Cases/Reservations")}
          </>
        ) : (
          <p className="text-muted">No documents uploaded yet.</p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentMasterModal;